import React from 'react';

/**
 * StatCard - Reusable component for displaying statistics
 * 
 * @param {Object} props
 * @param {React.Component} props.icon - Icon component to display
 * @param {string} props.label - Label text for the stat
 * @param {string|number} props.value - Value to display
 * @param {string} props.subtext - Optional subtext below the value
 * @param {boolean} props.clickable - Whether the card is clickable
 * @param {Function} props.onClick - Click handler
 * @param {string} props.className - Additional CSS classes
 */
const StatCard = ({ 
  icon: Icon, 
  label, 
  value, 
  subtext, 
  clickable = false, 
  onClick,
  className = ""
}) => {
  const baseClasses = "bg-blue-900/90 rounded-lg p-3";
  const clickableClasses = clickable 
    ? "cursor-pointer hover:bg-blue-800 hover:ring-2 hover:ring-yellow-500 transition-all duration-200" 
    : "";
  
  return (
    <div 
      className={`${baseClasses} ${clickableClasses} ${className}`}
      onClick={clickable ? onClick : undefined}
    >
      <div className="flex items-center space-x-2">
        {Icon && <Icon className="w-5 h-5 text-yellow-400" />}
        <div>
          <p className="text-sm font-medium text-white">{label}</p>
          <p className="text-lg font-bold text-white">{value}</p>
        </div>
      </div>
      {subtext && (
        <p className="text-xs text-yellow-300 mt-1">{subtext}</p>
      )}
    </div>
  );
};

/**
 * StreakCard - Specialized stat card for displaying streaks
 * 
 * @param {Object} props
 * @param {string} props.category - Category name (e.g., "affirmations")
 * @param {number} props.streak - Number of days in the streak
 * @param {React.Component} props.icon - Icon component to display
 */
export const StreakCard = ({ category, streak, icon: Icon }) => {
  return (
    <div className="flex items-center space-x-2 bg-blue-900/90 rounded-lg p-3">
      <Icon className="w-5 h-5 text-yellow-400" />
      <div>
        <p className="text-sm font-medium text-white capitalize">{category}</p>
        <div className="flex items-center space-x-1">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className="text-orange-500"
          >
            <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path>
          </svg>
          <span className="text-sm font-bold text-orange-600">{streak} days</span>
        </div>
      </div>
    </div>
  );
};

export default StatCard;
